import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { apiError } from '../../../admin/api/apiError'
import { useDealerToast } from '../../components/Toast/DealerToast'
import { deleteProfileLogo, getProfile, updateProfile, uploadProfileLogo } from '../../services/profileService'
import type { DealerProfile } from '../../types'
import './ProfilePage.css'

const LOGO_MAX_BYTES = 1024 * 1024
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp']
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// мягкая проверка до бэка: «+», цифры, пробелы, скобки, дефисы; точную валидацию делает сервер
const PHONE_RE = /^\+?[\d\s()-]{7,20}$/

type FormKey = 'displayName' | 'address' | 'phone' | 'email'
type FormState = Record<FormKey, string>

function toForm(p: DealerProfile): FormState {
  return {
    displayName: p.displayName ?? '',
    address: p.address ?? '',
    phone: p.phone ?? '',
    email: p.email ?? '',
  }
}

// язык отчёта клиенту: код локали → название на языке интерфейса
function languageName(locale: string | null, lang: string): string {
  if (!locale) return '—'
  try {
    const name = new Intl.DisplayNames([lang], { type: 'language' }).of(locale)
    return name ? name.charAt(0).toUpperCase() + name.slice(1) : locale.toUpperCase()
  } catch {
    return locale.toUpperCase()
  }
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation()
  const { showToast } = useDealerToast()

  const [profile, setProfile] = useState<DealerProfile | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState<FormState>({ displayName: '', address: '', phone: '', email: '' })
  const [errors, setErrors] = useState<Partial<Record<FormKey | 'general', string>>>({})
  const [saving, setSaving] = useState(false)
  const [logoBusy, setLogoBusy] = useState(false)
  const [logoError, setLogoError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    let cancelled = false
    setLoadError(false)
    getProfile()
      .then((p) => {
        if (cancelled) return
        setProfile(p)
        setForm(toForm(p))
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[ERROR] Failed to load dealer profile', err)
        setLoadError(true)
      })
    return () => { cancelled = true }
  }, [reloadKey])

  const initial = profile ? toForm(profile) : null
  const dirty = !!initial && (Object.keys(form) as FormKey[]).some((k) => form[k].trim() !== initial[k].trim())

  function setField(key: FormKey, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined, general: undefined }))
  }

  function validate(): boolean {
    const e: Partial<Record<FormKey, string>> = {}
    if (!form.address.trim()) e.address = t('dealer.profile.error_address_required')
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim())) e.phone = t('dealer.profile.error_phone_invalid')
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) e.email = t('dealer.profile.error_email_invalid')
    setErrors(e)
    return Object.keys(e).length === 0
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setSaving(true)
    try {
      // пустые поля отправляем как null: displayName=null — показывать клиентам внутреннее название
      const updated = await updateProfile({
        displayName: form.displayName.trim() || null,
        address: form.address.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      })
      setProfile(updated)
      setForm(toForm(updated))
      showToast(t('dealer.profile.saved'))
    } catch (err) {
      const { status, code, message } = apiError(err)
      // VALIDATION_ERROR может прийти с details.<поле>
      const details = axios.isAxiosError(err)
        ? (err.response?.data as { details?: Record<string, string> } | undefined)?.details
        : undefined
      if (code === 'PHONE_INVALID') setErrors({ phone: t('dealer.profile.error_phone_invalid') })
      else if (code === 'EMAIL_INVALID') setErrors({ email: t('dealer.profile.error_email_invalid') })
      else if (code === 'ADDRESS_REQUIRED' || (code === 'VALIDATION_ERROR' && details?.address)) {
        setErrors({ address: t('dealer.profile.error_address_required') })
      } else if (code === 'VALIDATION_ERROR') setErrors({ general: message || t('dealer.profile.error_validation') })
      else if (!status) setErrors({ general: t('dealer.common.error_network') })
      else setErrors({ general: t('dealer.profile.error_save') })
      console.error('[ERROR] Failed to save dealer profile', err)
    } finally {
      setSaving(false)
    }
  }

  async function handleLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setLogoError('')
    if (!LOGO_TYPES.includes(file.type)) {
      setLogoError(t('dealer.profile.logo_unsupported_type'))
      return
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoError(t('dealer.profile.logo_too_large'))
      return
    }
    setLogoBusy(true)
    try {
      const updated = await uploadProfileLogo(file)
      setProfile((prev) => (prev ? { ...prev, logoUrl: updated.logoUrl } : updated))
      showToast(t('dealer.profile.logo_uploaded'))
    } catch (err) {
      const { status, code } = apiError(err)
      if (code === 'LOGO_TOO_LARGE') setLogoError(t('dealer.profile.logo_too_large'))
      else if (code === 'LOGO_UNSUPPORTED_TYPE') setLogoError(t('dealer.profile.logo_unsupported_type'))
      else setLogoError(status ? t('dealer.profile.logo_upload_failed') : t('dealer.common.error_network'))
      console.error('[ERROR] Failed to upload dealer logo', err)
    } finally {
      setLogoBusy(false)
    }
  }

  async function handleLogoDelete() {
    if (!confirm(t('dealer.profile.logo_delete_confirm'))) return
    setLogoError('')
    setLogoBusy(true)
    try {
      await deleteProfileLogo()
      setProfile((prev) => (prev ? { ...prev, logoUrl: null } : prev))
      showToast(t('dealer.profile.logo_deleted'))
    } catch (err) {
      const { status, code } = apiError(err)
      // логотипа уже нет — для пользователя результат тот же
      if (code === 'LOGO_NOT_FOUND') {
        setProfile((prev) => (prev ? { ...prev, logoUrl: null } : prev))
      } else {
        setLogoError(status ? t('dealer.profile.logo_delete_failed') : t('dealer.common.error_network'))
        console.error('[ERROR] Failed to delete dealer logo', err)
      }
    } finally {
      setLogoBusy(false)
    }
  }

  if (loadError) {
    return (
      <div>
        <ProfileHeader />
        <div className="dealer-card dealer-empty">
          <p>{t('dealer.profile.load_failed')}</p>
          <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
            {t('dealer.common.retry')}
          </button>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div>
        <ProfileHeader />
        <div className="dealer-card dealer-empty"><div className="dealer-spinner dealer-profile__spinner" /></div>
      </div>
    )
  }

  function field(key: FormKey, opts: { type?: string; placeholder?: string; hint?: string; required?: boolean; autoComplete?: string }) {
    const id = `dealer-profile-${key}`
    return (
      <div className="dealer-field">
        <label className="dealer-label" htmlFor={id}>
          {t(`dealer.profile.label_${key}`)}
          {opts.required && <span className="dealer-profile__required" aria-hidden="true"> *</span>}
        </label>
        <input
          id={id}
          className={`dealer-input${errors[key] ? ' dealer-input--error' : ''}`}
          type={opts.type ?? 'text'}
          value={form[key]}
          placeholder={opts.placeholder}
          autoComplete={opts.autoComplete ?? 'off'}
          aria-invalid={!!errors[key]}
          aria-describedby={`${id}-hint`}
          onChange={(e) => setField(key, e.target.value)}
          disabled={saving}
        />
        {errors[key]
          ? <span id={`${id}-hint`} className="dealer-field-error">{errors[key]}</span>
          : opts.hint && <span id={`${id}-hint`} className="dealer-hint">{opts.hint}</span>}
      </div>
    )
  }

  return (
    <div>
      <ProfileHeader />

      <div className="dealer-profile">
        <form className="dealer-card dealer-profile__form" onSubmit={handleSubmit} noValidate>
          <div>
            <h2 className="dealer-profile__card-title">{t('dealer.profile.card_details')}</h2>
            <p className="dealer-hint">{t('dealer.profile.card_details_hint')}</p>
          </div>

          {field('displayName', { placeholder: profile.name, hint: t('dealer.profile.hint_displayName'), autoComplete: 'organization' })}
          {field('address', { required: true, autoComplete: 'street-address' })}
          <div className="dealer-profile__row">
            {field('phone', { type: 'tel', placeholder: '+1 555 123 4567', autoComplete: 'tel' })}
            {field('email', { type: 'email', placeholder: 'service@dealer.com', autoComplete: 'email' })}
          </div>

          {errors.general && <div className="dealer-alert dealer-alert--error" role="alert">{errors.general}</div>}

          <div className="dealer-profile__actions">
            <button type="submit" className="dealer-btn dealer-btn--primary" disabled={saving || !dirty}>
              {saving ? t('dealer.profile.saving') : t('dealer.profile.save')}
            </button>
            {dirty && !saving && (
              <button
                type="button"
                className="dealer-btn dealer-btn--secondary"
                onClick={() => { setForm(toForm(profile)); setErrors({}) }}
              >
                {t('dealer.profile.discard')}
              </button>
            )}
          </div>
        </form>

        <div className="dealer-profile__side">
          <section className="dealer-card">
            <h2 className="dealer-profile__card-title">{t('dealer.profile.card_logo')}</h2>
            <div className="dealer-profile__logo">
              {profile.logoUrl
                ? <img src={profile.logoUrl} alt={profile.displayName || profile.name} />
                : <span className="dealer-muted">{t('dealer.profile.logo_empty')}</span>}
            </div>
            <p className="dealer-hint">{t('dealer.profile.logo_hint')}</p>
            {logoError && <div className="dealer-alert dealer-alert--error dealer-profile__logo-error" role="alert">{logoError}</div>}
            <div className="dealer-profile__actions">
              <input ref={fileRef} type="file" accept={LOGO_TYPES.join(',')} hidden onChange={handleLogoFile} />
              <button type="button" className="dealer-btn dealer-btn--secondary dealer-btn--sm" disabled={logoBusy} onClick={() => fileRef.current?.click()}>
                {logoBusy ? t('dealer.profile.logo_uploading') : profile.logoUrl ? t('dealer.profile.logo_replace') : t('dealer.profile.logo_upload')}
              </button>
              {profile.logoUrl && (
                <button type="button" className="dealer-text-btn dealer-profile__remove" disabled={logoBusy} onClick={handleLogoDelete}>
                  {t('dealer.profile.logo_remove')}
                </button>
              )}
            </div>
          </section>

          <section className="dealer-card">
            <h2 className="dealer-profile__card-title">{t('dealer.profile.card_account')}</h2>
            <dl className="dealer-profile__readonly">
              <dt>{t('dealer.profile.label_internal_name')}</dt>
              <dd>{profile.name}</dd>
              <dt>{t('dealer.profile.label_report_language')}</dt>
              <dd>{languageName(profile.locale, i18n.language)}</dd>
            </dl>
            <p className="dealer-hint">{t('dealer.profile.readonly_hint')}</p>
          </section>
        </div>
      </div>
    </div>
  )
}

function ProfileHeader() {
  const { t } = useTranslation()
  return (
    <div className="dealer-page__header">
      <h1 className="dealer-page__title">{t('dealer.profile.title')}</h1>
      <p className="dealer-page__subtitle">{t('dealer.profile.subtitle')}</p>
    </div>
  )
}
